// multiMonitorDetector — flag candidates with an external monitor
// connected (feature_key: `lockdown.multi_monitor_detect`).
//
// Why: a second monitor is the most common way a candidate reads from
// an AI assistant / notes without it showing in the webcam frame. This
// detector doesn't BLOCK candidates with multiple monitors (some have
// them legitimately for laptops + dock), it just flags it so the
// recruiter knows.
//
// Detection strategy (best to least reliable):
//   1. Window Management API (`window.getScreenDetails()`) — Chrome 100+,
//      requires permission. Most accurate. Returns the actual screen
//      array so we can count.
//   2. screen.availWidth heuristic — total span of all displays as
//      reported by the OS. If wider than a single common monitor
//      (>2560px after accounting for typical bezels), likely multi-mon.
//   3. screen.width vs window.outerWidth ratio — also heuristic.
//
// For v1 we try (1) if available without auto-prompting (it requires a
// user gesture for the prompt, which we don't want to demand), and fall
// back to the heuristic (2).
//
// One-shot at test start, plus a re-check every 60s in case the candidate
// plugs in a monitor mid-test.

const FEATURE_MULTI_MONITOR = 'lockdown.multi_monitor_detect';

// Width above which we assume more than one display is in play. 2560px
// is wider than a single typical 1080p / 1440p monitor accounting for
// some HiDPI scaling fudge. Tunable via threshold JSON.
const DEFAULT_SUSPICIOUS_WIDTH = 2560;

const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};


/**
 * Detect multi-monitor setup. Returns { detected, method, screenCount, span }.
 * `method` indicates which signal triggered:
 *   'api'        — Window Management API confirmed >1 screen
 *   'width-span' — screen.availWidth exceeds suspicious threshold
 *   'none'       — single monitor confirmed
 */
async function detectMultipleMonitors(suspiciousWidth) {
  // Method 1: Window Management API (Chrome / Edge 100+).
  // Only use if permission is ALREADY granted — we don't want to prompt
  // the candidate (would be intrusive). The Permissions API tells us
  // whether 'window-management' is granted without prompting.
  try {
    if (window.getScreenDetails && navigator.permissions?.query) {
      const perm = await navigator.permissions.query({ name: 'window-management' })
        .catch(() => null);
      if (perm && perm.state === 'granted') {
        const details = await window.getScreenDetails();
        if (details && Array.isArray(details.screens)) {
          return {
            detected: details.screens.length > 1,
            method: 'api',
            screenCount: details.screens.length,
            span: null,
          };
        }
      }
    }
  } catch (_) {
    // API errored — fall through to heuristic.
  }

  // Method 2: width-span heuristic. screen.availWidth covers the total
  // virtual desktop span on most browsers when multiple monitors are
  // arranged side-by-side. May UNDERCOUNT vertically-stacked monitors.
  const span = (window.screen && window.screen.availWidth) || 0;
  if (span >= suspiciousWidth) {
    return {
      detected: true,
      method: 'width-span',
      screenCount: null,
      span,
    };
  }

  return { detected: false, method: 'none', screenCount: null, span };
}


export function createMultiMonitorDetector({
  emit,
  getFlag,
  getThreshold,
}) {
  if (typeof emit !== 'function') {
    throw new Error('[multiMonitorDetector] emit must be a function');
  }

  let running = false;
  let intervalId = null;
  let alreadyReported = false;

  const runCheck = async () => {
    if (!running || !getFlag(FEATURE_MULTI_MONITOR)) return;

    const thr = getThreshold(FEATURE_MULTI_MONITOR);
    const suspiciousWidth = num(thr, 'suspicious_width_px', DEFAULT_SUSPICIOUS_WIDTH);

    const result = await detectMultipleMonitors(suspiciousWidth);
    if (!result.detected) return;

    // Don't re-emit the same finding every recheck — that floods the log.
    // We DO want to know if a NEW monitor is plugged in mid-test, but our
    // heuristic can't distinguish "started with 2" vs "now has 3", so for
    // v1 we just emit once per session.
    if (alreadyReported) return;
    alreadyReported = true;

    emit({
      feature_key: FEATURE_MULTI_MONITOR,
      severity: 'medium',
      score_delta: 6,
      message: 'Multiple monitors detected — possible secondary screen for notes',
      evidence: {
        stage: 'flag',
        method: result.method,
        screen_count: result.screenCount,
        screen_span_px: result.span,
        suspicious_width_threshold: suspiciousWidth,
      },
    });
  };

  const start = async () => {
    if (running) return;
    running = true;
    await runCheck();
    const thr = getThreshold(FEATURE_MULTI_MONITOR);
    const recheckSeconds = num(thr, 'recheck_seconds', 60);
    intervalId = setInterval(() => { void runCheck(); }, recheckSeconds * 1000);
  };

  const stop = () => {
    running = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    alreadyReported = false;
  };

  return { start, stop, isReady: () => running };
}
